import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { IconType } from 'react-icons';
import type { PolymorphicComponentProps } from '@/utils/polymorphic';
import { recursiveCloneChildren } from '@/utils/recursive-clone-children';
import { tv, type VariantProps } from '@/utils/tv';
import { AUTOCOMPLETE_PASSWORD_MANAGERS_OFF } from '../../utils/constants';

const INPUT_ROOT_NAME = 'InputRoot';
const INPUT_WRAPPER_NAME = 'InputWrapper';
const INPUT_EL_NAME = 'InputEl';
const INPUT_ICON_NAME = 'InputIcon';
const INPUT_AFFIX_NAME = 'InputAffixButton';
const INPUT_INLINE_AFFIX_NAME = 'InputInlineAffixButton';

export const inputVariants = tv({
  slots: {
    root: [
      // base
      'ring-stroke-soft',
      'group relative flex w-full overflow-hidden bg-bg-white text-text-strong shadow-xs',
      'transition duration-200 ease-out',
      'divide-x divide-stroke-soft',
      // before
      'before:absolute before:inset-0 before:ring-1 before:ring-inset before:ring-stroke-soft',
      'before:pointer-events-none before:rounded-[inherit]',
      'before:transition before:duration-200 before:ease-out',
      // hover
      'hover:shadow-none',
      // focus
      'has-[input:focus]:border-stroke-soft has-[input:focus]:ring-stroke-soft/50 has-[input:focus]:ring-[3px]',
      'focus-within:border-stroke-soft focus-within:ring-stroke-soft/50 focus-within:ring-[3px]',
      // disabled
      'has-[input:disabled]:shadow-none',
      // aria-invalid
      'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
    ],
    wrapper: [
      // base
      'group/input-wrapper flex w-full cursor-text items-center bg-bg-white',
      'transition duration-200 ease-out',
      // hover
      'hover:[&:not(&:has(input:focus))]:bg-bg-weak',
      // disabled
      'has-[input:disabled]:pointer-events-none has-[input:disabled]:bg-bg-weak',
    ],
    input: [
      // base
      'w-full bg-transparent bg-none text-paragraph-sm text-text-strong outline-hidden',
      'transition duration-200 ease-out',
      // horizontal scroll with fade gradient
      'overflow-x-auto scrollbar-thin',
      'mask-[linear-gradient(to_right,black_calc(100%-1.5rem),transparent)]',
      'mask-size-[100%_100%]',
      'mask-no-repeat',
      // placeholder
      'placeholder:select-none placeholder:text-text-soft placeholder:transition placeholder:duration-200 placeholder:ease-out',
      // hover placeholder
      'group-hover/input-wrapper:placeholder:text-text-sub',
      // focus
      'focus:outline-hidden',
      // focus placeholder
      'group-has-[input:focus]:placeholder:text-text-sub',
      // disabled
      'disabled:text-text-disabled disabled:placeholder:text-text-disabled',
    ],
    icon: [
      // base
      'flex size-5 shrink-0 select-none items-center justify-center',
      'transition duration-200 ease-out',
      // placeholder state
      'group-has-placeholder-shown:text-text-soft',
      // filled state
      'text-text-sub',
      // hover
      'group-hover/input-wrapper:group-has-placeholder-shown:text-text-sub',
      // focus
      'group-has-[input:focus]/input-wrapper:group-has-placeholder-shown:text-text-sub',
      // disabled
      'group-has-[input:disabled]/input-wrapper:text-text-disabled',
    ],
    affix: [
      // base
      'shrink-0 bg-bg-white text-paragraph-sm text-text-sub',
      'flex items-center justify-center truncate',
      'transition duration-200 ease-out',
      // placeholder state
      'group-has-placeholder-shown:text-text-soft',
      // focus state
      'group-has-[input:focus]:group-has-placeholder-shown:text-text-sub',
    ],
    inlineAffix: [
      // base
      'text-paragraph-sm text-text-sub',
      // placeholder state
      'group-has-placeholder-shown:text-text-soft',
      // focus state
      'group-has-[input:focus]:group-has-placeholder-shown:text-text-sub',
    ],
  },
  variants: {
    size: {
      md: {
        root: 'rounded-10',
        wrapper: 'gap-2 px-3',
        input: 'h-10',
      },
      sm: {
        root: 'rounded-lg',
        wrapper: 'gap-2 px-2.5',
        input: 'h-[2.35rem] text-paragraph-xs',
        affix: 'text-paragraph-xs',
        inlineAffix: 'text-paragraph-xs',
      },
      xs: {
        root: 'rounded-lg',
        wrapper: 'gap-1.5 px-2',
        input: 'h-8 text-paragraph-xs',
        affix: 'text-paragraph-xs',
        inlineAffix: 'text-paragraph-xs',
        icon: 'size-4',
      },
      '2xs': {
        root: 'rounded-lg',
        wrapper: 'gap-1.5 px-2',
        input: 'h-7 text-paragraph-xs',
        icon: 'size-4',
      },
    },
    hasError: {
      true: {
        root: [
          // base
          'before:ring-error-base',
          // base
          'hover:before:ring-error-base [&:not(&:has(input:focus)):has(>:only-child)]:hover:before:ring-error-base',
          // focus
          'has-[input:focus]:border-destructive has-[input:focus]:ring-destructive/20 dark:has-[input:focus]:ring-destructive/40',
          'focus-within:border-destructive focus-within:ring-destructive/20 dark:focus-within:ring-destructive/40',
        ],
      },
      false: {
        root: [],
      },
    },
  },
  compoundVariants: [
    //#region affix
    {
      size: 'md',
      class: {
        affix: 'px-3',
      },
    },
    {
      size: ['sm', 'xs'],
      class: {
        affix: 'px-2.5',
      },
    },
    //#endregion
  ],
  defaultVariants: {
    size: 'sm',
  },
});

type InputSharedProps = VariantProps<typeof inputVariants>;

const InputRoot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    InputSharedProps & {
      asChild?: boolean;
    }
>(({ className, children, size, hasError, asChild, ...rest }, ref) => {
  const uniqueId = React.useId();
  const Component = asChild ? Slot : 'div';

  const { root } = inputVariants({
    size,
    hasError,
  });

  const sharedProps: InputSharedProps = {
    size,
    hasError,
  };

  const extendedChildren = recursiveCloneChildren(
    children as React.ReactElement[],
    sharedProps,
    [INPUT_WRAPPER_NAME, INPUT_EL_NAME, INPUT_ICON_NAME, INPUT_AFFIX_NAME, INPUT_INLINE_AFFIX_NAME],
    uniqueId,
    asChild
  );

  return (
    <Component ref={ref} className={root({ class: className })} {...rest}>
      {extendedChildren}
    </Component>
  );
});
InputRoot.displayName = INPUT_ROOT_NAME;

function InputWrapper({
  className,
  children,
  size,
  hasError,
  asChild,
  ...rest
}: React.HTMLAttributes<HTMLLabelElement> &
  InputSharedProps & {
    asChild?: boolean;
  }) {
  const Component = asChild ? Slot : 'label';

  const { wrapper } = inputVariants({
    size,
    hasError,
  });

  return (
    <Component className={wrapper({ class: className })} {...rest}>
      {children}
    </Component>
  );
}

InputWrapper.displayName = INPUT_WRAPPER_NAME;

const InputEl = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> &
    InputSharedProps & {
      asChild?: boolean;
      onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }
>(({ className, type = 'text', size, hasError, asChild, ...rest }, forwardedRef) => {
  const Component = asChild ? Slot : 'input';

  const { input } = inputVariants({
    size,
    hasError,
  });

  return (
    <Component
      type={type}
      className={input({ class: className })}
      ref={forwardedRef}
      {...AUTOCOMPLETE_PASSWORD_MANAGERS_OFF}
      {...rest}
    />
  );
});
InputEl.displayName = INPUT_EL_NAME;

type InputProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> &
  InputSharedProps &
  Omit<React.ComponentPropsWithoutRef<typeof InputEl>, 'size'> & {
    leadingIcon?: IconType;
    trailingIcon?: IconType;
    leadingNode?: React.ReactNode;
    trailingNode?: React.ReactNode;
    inlineLeadingNode?: React.ReactNode;
    inlineTrailingNode?: React.ReactNode;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size,
      hasError,
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      leadingNode,
      trailingNode,
      inlineLeadingNode,
      inlineTrailingNode,
      onChange,
      ...rest
    },
    forwardedRef
  ) => {
    return (
      <InputRoot size={size} hasError={hasError}>
        {leadingNode && <div className="flex flex-col justify-center gap-1">{leadingNode}</div>}
        <InputWrapper>
          {inlineLeadingNode}
          {LeadingIcon && <InputIcon as={LeadingIcon} />}
          <InputEl ref={forwardedRef} type="text" onChange={onChange} {...rest} />
          {TrailingIcon && <InputIcon as={TrailingIcon} />}
          {inlineTrailingNode}
        </InputWrapper>
        {trailingNode && <div className="flex flex-col justify-center gap-1">{trailingNode}</div>}
      </InputRoot>
    );
  }
);

Input.displayName = 'Input';

function InputIcon<T extends React.ElementType = 'div'>({
  size,
  hasError,
  as,
  className,
  ...rest
}: PolymorphicComponentProps<T, { size?: 'md' | 'sm' | 'xs' } & Omit<InputSharedProps, 'size'>>) {
  const Component = as || 'div';
  const { icon } = inputVariants({ size, hasError });

  return <Component className={icon({ class: className })} {...rest} />;
}

InputIcon.displayName = INPUT_ICON_NAME;

function InputAffix({
  className,
  children,
  size,
  hasError,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & InputSharedProps) {
  const { affix } = inputVariants({
    size,
    hasError,
  });

  return (
    <div className={affix({ class: className })} {...rest}>
      {children}
    </div>
  );
}

InputAffix.displayName = INPUT_AFFIX_NAME;

function InputInlineAffix({
  className,
  children,
  size,
  hasError,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & InputSharedProps) {
  const { inlineAffix } = inputVariants({
    size,
    hasError,
  });

  return (
    <span className={inlineAffix({ class: className })} {...rest}>
      {children}
    </span>
  );
}

InputInlineAffix.displayName = INPUT_INLINE_AFFIX_NAME;

export {
  InputAffix as Affix,
  InputIcon as Icon,
  InputInlineAffix as InlineAffix,
  Input,
  InputEl as InputPure,
  InputRoot,
  InputWrapper,
  type InputProps,
};
